import { Compass, Layers3, Shield } from 'lucide-react'
import OpenClawLandingPage from '../components/openclaw/OpenClawLandingPage'
import Baidu2DownloadButtons from '../components/openclaw/Baidu2DownloadButtons'
import useBaidu2Tracking from '../hooks/useBaidu2Tracking'

function OpenClawOfficial() {
  const { bdVid, landingUrl } = useBaidu2Tracking('official')

  return (
    <OpenClawLandingPage
      downloadContent={<Baidu2DownloadButtons theme="rose" bdVid={bdVid} landingUrl={landingUrl} landingPage="official" />}
      theme="rose"
      source="openclaw-official"
      seo={{
        title: 'OpenClaw中文官网 龙虾AI桌面助手',
        description: '想找OpenClaw官网和OpenClaw中文官网？这里可快速了解龙虾AI桌面助手的产品能力、适用场景、新手上手方式与官方下载入口。',
        keywords: 'OpenClaw官网, OpenClaw中文官网, 龙虾AI, OpenClaw下载, OpenClaw怎么用'
      }}
      hero={{
        eyebrow: 'OpenClaw 中文官网入口',
        title: 'OpenClaw 中文官网入口',
        subtitle: '龙虾AI桌面助手：能力、场景、下载与新手上手，一页看懂',
        description:
          '你可能在找 OpenClaw 官网 / OpenClaw 中文官网，也可能只是想判断它到底适不适合自己。\n这里把“是什么、能做什么、适合谁、怎么开始用”按阅读顺序整理好，让你看完就能直接进入下载和上手。',
        leadBullets: ['先了解定位与典型场景', '再直达官方下载入口', '最后按上手步骤跑通第一轮体验'],
        tags: ['官网入口', '产品能力', '应用场景', '新手上手'],
        guideTitle: '从安装到使用，OpenClaw 的体验是怎样的？',
        guideDescription: 'OpenClaw 面向的是想快速获得桌面 AI 助手体验的用户。它不是单一的聊天页面，而是更偏“桌面操作 + AI 能力 + 工作流程辅助”的产品形态。',
        guideBullets: [
          '先通过官方下载入口选择适合自己系统的版本。',
          '安装完成后即可进入桌面助手界面，查看基础能力与入口。',
          '通过技能、操作辅助与常见场景引导，逐步熟悉 OpenClaw 的使用方式。'
        ]
      }}
      screenshotCaption="截图仅用于帮助你快速理解 OpenClaw 的桌面交互方式，真正使用时会根据本机环境与功能入口展示不同面板。"
      highlights={[
        {
          icon: Layers3,
          title: '不只是聊天，而是可落地的桌面助手',
          description: 'OpenClaw 更强调桌面端的 AI 体验，把常见操作辅助、内容整理、信息处理与工作流入口结合在一起。'
        },
        {
          icon: Compass,
          title: '适合想快速了解并直接试用的人',
          description: '如果你不想先看一堆复杂文档，而是希望先了解官网、核心能力和下载安装方式，这个页面就是最短路径。'
        },
        {
          icon: Shield,
          title: '官网信息、下载入口与上手指引集中呈现',
          description: '不用来回找入口。你可以在这里直接了解 OpenClaw 是什么、能做什么，以及下载之后该怎么开始。'
        }
      ]}
      steps={[
        {
          title: '先确认产品定位',
          description: 'OpenClaw 是面向桌面使用场景的 AI 助手工具，重点是帮助用户更自然地接入 AI，而不是仅提供单一对话框。'
        },
        {
          title: '再看典型使用场景',
          description: '适合用来了解桌面端 AI 助手的工作方式，例如信息查看、流程辅助、安装后的基础操作体验。'
        },
        {
          title: '最后直接下载安装体验',
          description: '如果你已经对产品定位有概念，可以直接通过页面底部的下载入口安装 OpenClaw，然后按页面提示开始体验。'
        }
      ]}
      sections={[
        {
          title: 'OpenClaw 中文官网用户最关心什么？',
          description: '从搜索意图看，用户通常不是只想看一句介绍，而是想快速知道是否有官网、是否能下载、安装后怎么上手。',
          items: [
            '有没有 OpenClaw 官网或 OpenClaw 中文官网入口。',
            'OpenClaw 到底是什么，和普通 AI 工具有什么区别。',
            '下载后是否容易上手，是否需要复杂配置。',
            '是否适合日常工作、效率场景或个人体验。'
          ]
        },
        {
          title: 'OpenClaw 更适合哪些人先体验？',
          description: '如果你希望先从“看得到界面、能直接安装、能快速体验”的路径了解 OpenClaw，那么你属于非常适合先试用的人群。',
          items: [
            '想快速了解龙虾AI桌面助手，而不是先读技术文档的人。',
            '想先通过官网下载入口和安装体验判断产品是否适合自己的人。',
            '希望从桌面端视角理解 OpenClaw，而不是只看抽象概念的人。',
            '需要一个从“了解产品”到“开始试用”衔接自然的落地页。'
          ]
        }
      ]}
      faq={[
        {
          q: 'OpenClaw 官网和 OpenClaw 中文官网是一个概念吗？',
          a: '大多数用户搜索这两个词，本质上都是在找官方入口、中文介绍、下载方式与上手说明。这个页面会把这几类信息放在一起。'
        },
        {
          q: 'OpenClaw 下载后能马上用吗？',
          a: '页面底部提供的是官网主站复用的下载逻辑。下载安装后，你可以先从基础界面和常用入口开始体验。'
        },
        {
          q: '这个页面适合第一次接触 OpenClaw 的人吗？',
          a: '适合。页面内容是按“先了解产品，再进入下载和上手”来设计的，尽量减少第一次接触时的信息负担。'
        },
        {
          q: '龙虾AI和 OpenClaw 是什么关系？',
          a: '这里会把龙虾AI视作 OpenClaw 的中文认知入口之一，帮助用户把英文产品名和中文印象对应起来。'
        }
      ]}
      closing={{
        title: '准备直接体验 OpenClaw？',
        description: '如果你已经确认想进一步了解 OpenClaw 官网入口、产品能力与上手体验，现在就可以下载适合你设备的版本开始试用。',
        footerTitle: '先了解，再下载，再上手',
        footerDescription: '这个页面的目标不是只给你一个下载按钮，而是让你在真正安装 OpenClaw 之前，先把核心信息看明白，再更顺畅地开始体验。',
        footerTags: ['OpenClaw中文官网', '龙虾AI桌面助手', '官方下载入口', '新手上手指南']
      }}
    />
  )
}

export default OpenClawOfficial
